import { css } from '@emotion/react';

import { useId } from '@toolbox/hooks';
import { makeStyles } from '@toolbox/style';
import { Step } from '@widget/core';
import { Box, Grid, GridItem, InfoIconWithTooltip, Typography } from '../toolbox/components';
import { richTextResolver } from '../storyblok/rteResolver';
import {
  ActionButton,
  CheckboxField,
  // CheckboxField,
  PhoneNumberField,
  QuestionLayout,
  SwitchField,
  TextInputField,
} from '../components';

import type { StepProps } from '@widget/core';
import type { ReactNode } from 'react';
import type { Richtext } from 'storyblok-js-client';

const useStyles = makeStyles(({ theme }) => ({
  root: css`
    display: flex;
  `,
  form: css`
    margin: auto;
  `,
  footer: css`
    text-align: center;
  `,
  actionBtn: css`
    max-width: 438px;

    &.large {
      line-height: ${theme.lineHeights.md};
    }
  `,
  actionBtnSubText: css`
    font-size: ${theme.fontSizes.f3};
    font-weight: ${theme.fontWeights.normal};
  `,
  disclaimer: css`
    color: ${theme.palette.colors.gray['400']};
    margin-top: ${theme.spacing(4)};
    margin-left: auto;
    margin-right: auto;
    max-width: 538px;

    a {
      text-decoration: none;
      color: inherit;
      cursor: pointer;
      font-weight: ${theme.fontWeights.bold};
      padding: ${theme.spacing(1)};
      border-bottom: 3px solid ${theme.palette.colors.yellow[400]};
    }
  `,
}));

export type ContactForm = {
  name?: string;
  firstName: string;
  lastName: string;
  salutation: string;
  email: string;
  phone: string;
};

type ContactStepFields = Partial<{
  title: string;
  description: string;
  nameLabel: string;
  firstNameLabel: string;
  lastNameLabel: string;
  phoneLabel: string;
  phoneTooltip: string;
  emailLabel: string;
  emailTooltip: string;
  buttonText: string;
  buttonSubText: string;
  disclaimerText: Richtext | string;
  closeButtonText?: string;
  salutationOptions?: string[];
  icon?: ReactNode;
  socialProofMessage?: Richtext;
  newsletterText?: string;
  isNewsletterOptInShown?: boolean;
}>;

type ContactStepProps = StepProps & ContactStepFields;

const defaultValues: ContactStepFields = {
  title: 'Wer soll die kostenlose Immobilienbewertung erhalten?',
  description:
    'Im erstellten Bericht wird Ihnen der Wert Ihrer Immobilie genannt sowie erklärt wie wir auf diesen Wert kommen.',
  nameLabel: 'Name',
  firstNameLabel: 'Vorname',
  lastNameLabel: 'Nachname',
  phoneLabel: 'Telefonnummer',
  phoneTooltip:
    'Wir melden uns in Kürze bei Ihnen, um Angaben für eine optimierte Bewertung zu erfragen und ein Verkaufs- oder Kaufinteresse zu besprechen',
  emailLabel: 'E-Mail-Adresse',
  emailTooltip:
    'Sie erhalten in Kürze Ihre persönliche Immobilienbewertung sowie weitere interessante Angebote auf die hinterlegte E-Mail-Adresse',
  buttonText: 'Bewertung erhalten',
  buttonSubText: 'Kostenlos & Unverbindlich',
  newsletterText:
    'Ja, ich möchte von der McMakler GmbH aktuelle Nachrichten und Informationen zum Immobilienmarkt erhalten und über Aktionen und Produkte benachrichtigt werden. Ich kann mich jederzeit wieder abmelden.',
};

export const ContactStep = ({
  title = defaultValues.title,
  description = defaultValues.description,
  firstNameLabel = defaultValues.firstNameLabel,
  lastNameLabel = defaultValues.lastNameLabel,
  phoneLabel = defaultValues.phoneLabel,
  phoneTooltip = defaultValues.phoneTooltip,
  emailLabel = defaultValues.emailLabel,
  emailTooltip = defaultValues.emailTooltip,
  buttonText = defaultValues.buttonText,
  buttonSubText = defaultValues.buttonSubText,
  newsletterText = defaultValues.newsletterText,
  isNewsletterOptInShown,
  disclaimerText,
  icon,
  closeButtonText,
  salutationOptions = ['Herr', 'Frau'],
  socialProofMessage,
  ...stepProps
}: ContactStepProps): JSX.Element => {
  const classes = useStyles();

  const emailTooltipId = useId();
  const phoneTooltipId = useId();

  return (
    <Step {...stepProps}>
      <QuestionLayout
        title={title}
        closeButtonText={closeButtonText}
        description={description}
        maxW="xl"
        css={classes.root as any}
        socialProofMessage={socialProofMessage}
      >
        <div css={classes.form as any}>
          <Box mt={4} mb={8}>
            <SwitchField
              width="200px"
              name="contactForm.salutation"
              options={salutationOptions}
              required
              errorMessage="Bitte wählen Sie eine Anrede"
            />
          </Box>
          <Box mb={3}>
            <Grid justifyContent="space-between" alignItems="center">
              <GridItem md={6}>
                <TextInputField required name="contactForm.firstName" label={firstNameLabel} />
              </GridItem>
              <GridItem md={6}>
                <TextInputField required name="contactForm.lastName" label={lastNameLabel} />
              </GridItem>
            </Grid>
          </Box>
          <Box mb={3}>
            <TextInputField
              required
              rules={{
                email: true,
              }}
              aria-describedby={emailTooltipId}
              type="email"
              suffix={
                <InfoIconWithTooltip
                  id={emailTooltipId}
                  placement="left"
                  arrowCornerPadding={3}
                  text={emailTooltip}
                />
              }
              name="contactForm.email"
              label={emailLabel}
            />
          </Box>
          <Box mb={3}>
            <PhoneNumberField
              required
              aria-describedby={phoneTooltipId}
              name="contactForm.phone"
              label={phoneLabel}
              suffix={
                <InfoIconWithTooltip
                  id={phoneTooltipId}
                  placement="left"
                  arrowCornerPadding={3}
                  text={phoneTooltip}
                />
              }
            />
          </Box>
          {isNewsletterOptInShown && (
            <Box mb={6}>
              <CheckboxField
                name="contactForm.newsletterOptIn"
                label={<Typography variant="c1">{newsletterText}</Typography>}
              />
            </Box>
          )}
          <div css={classes.footer as any}>
            <ActionButton css={classes.actionBtn as any} fullWidth size="large" submit submitWidget>
              <div>{buttonText}</div>
              <div css={classes.actionBtnSubText as any}>{buttonSubText}</div>
            </ActionButton>
            <Typography
              variant="c2"
              css={classes.disclaimer as any}
              dangerouslySetInnerHTML={{
                __html:
                  typeof disclaimerText === 'string'
                    ? disclaimerText
                    : richTextResolver.render(disclaimerText as Richtext) || '',
              }}
            />
          </div>
        </div>
      </QuestionLayout>
    </Step>
  );
};
